import LibraryPendingBadge from "@/components/maintenance/LibraryPendingBadge";
import { Link, useLocation } from "wouter";
import { StaffAlertsUnread } from "@/components/staff/StaffAlerts";
import { StaffAttendancePendingBadge } from "@/components/staff/StaffTeamCheck";
import { MAINTENANCE_PAGES, maintenanceText } from "@/components/maintenance/language";
import { hasPermission, useAuth } from "@/lib/auth";
import { CreateWorkspaceMenuItem } from "./CreateWorkspaceMenuItem";
import { useTranslation } from "react-i18next";
import { cashText } from "@/lib/cash-language";
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Users, 
  MapPin, 
  BarChart3, 
  LogOut, 
  Menu, 
  Bell,
  UserCircle,
  FileText,
  Tags,
  Award,
  Settings,
  FileClock,
  ClipboardList,
  ChevronsUpDown,
  Check,
  Loader2,
  ChevronDown,
  ChevronRight,
  Ruler,
  ShieldAlert,
  Star,
  ArrowLeftRight,
  Boxes,
  Shield,
  RotateCcw,
  LineChart,
  GitCompareArrows,
  Activity,
  Grid3X3,
  Layers,
  CalendarClock,
  CalendarDays,
  ListOrdered,
  CalendarRange,
  Plus,
  Truck,
  ReceiptText,
  RefreshCw,
  Inbox,
  Trash2,
  Smartphone,
} from "lucide-react";
import { PwaInstallBanner } from "@/components/pwa/PwaInstallBanner";
import { PwaInstallDialog } from "@/components/pwa/PwaInstallDialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { deliveryKey, type DeliveryResponse } from "@/components/delivery-companies/use-delivery-companies";
import { useToast } from "@/hooks/use-toast";
import logo from "@assets/generated_images/logo_for_infrastock.png";
import { cn } from "@/lib/utils";
import React, { useState, useEffect, useRef, Suspense } from "react";
import { safeLazy as lazy } from "@/lib/safe-lazy";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useNotifications } from "@/hooks/use-notifications";
import { Badge } from "@/components/ui/badge";
import { NotificationRow } from "@/components/notifications/NotificationRow";
import { ReceivedBeforeApprovalCount } from "@/components/receipts/ReceivedBeforeApprovalCount";
import StaffPersonSearch from "@/components/staff/StaffPersonSearch";
import { StaffEmployeeOverlayProvider } from "@/components/staff/StaffEmployeeOverlay";
import { staffNavigationText as staffText } from "@/lib/staff-navigation-language";
import { StaffCertificateBadge } from "@/components/staff/StaffCertificateEntry";
import { useStaffTurnoverAccess } from "@/components/staff/staff-step18-access";
import { useStaffActivityAccess } from "@/components/staff/staff-step19-access";
import { useRequestCount } from "@/hooks/use-purchase-requests";
import { useProcessingCount } from "@/hooks/use-request-processing";
import { canOpenPeopleAccess } from "@/components/people-access/model";
import { useCanAccessPeopleAccess } from "@/components/people-access/usePeopleAccess";

const StaffStyles = lazy(() => import("@/components/staff/StaffStyles"));

export default function Layout({ children }: { children: React.ReactNode }) {
  const canTurnover=useStaffTurnoverAccess();
  const canActivity=useStaffActivityAccess();
  const [location, navigate] = useLocation();
  const { user, logout, roles, currentWorkspace, availableWorkspaces, switchWorkspace, refreshSession } = useAuth();
  const canEnterPeopleAccess = useCanAccessPeopleAccess();
  const { i18n, t } = useTranslation();
  const canViewPurchaseRequests = ["purchase_requests.view", "purchase_requests.process", "purchase_requests.view_all"].some(permission => hasPermission(user, permission, roles));
  const { data: purchaseRequestCount } = useRequestCount(currentWorkspace?.id || "", canViewPurchaseRequests);
  const canProcessPurchaseRequests = hasPermission(user, "purchase_requests.process", roles);
  const { data: processingCount } = useProcessingCount(currentWorkspace?.id || "", canProcessPurchaseRequests);
  const isDeliveryCompanies = location === "/delivery-companies" || location.startsWith("/delivery-companies/");
  const isDeliverySurface = isDeliveryCompanies || location === "/financials/branch-cash/cash-book";
  const deliveryCompanyId = isDeliveryCompanies ? location.split("/")[2] : undefined;
  // Subscribe to the page's cache only. The page owns the authenticated request.
  const { data: deliveryBreadcrumb } = useQuery<DeliveryResponse>({
    queryKey: [...deliveryKey(currentWorkspace?.id || ""), deliveryCompanyId || "list"],
    enabled: false,
  });
  const deliveryCompany = deliveryBreadcrumb?.companies.find(c => c.id === deliveryCompanyId);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications({ enabled: dropdownOpen, limit: 10 });
  const { toast } = useToast();

  // Personal sign-offs inbox — drives the sidebar "Sign-offs" entry + badge.
  // Only shown when stakeholder sign-offs are enabled for the workspace.
  const { data: signoffConfig } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/signoffs/config", currentWorkspace?.id],
    queryFn: () => api.get("/signoffs/config"),
    enabled: !!user && !!currentWorkspace?.id,
  });
  const signoffsEnabled = !!signoffConfig?.enabled;
  const { data: signoffInbox = [] } = useQuery<any[]>({
    queryKey: ["/api/signoffs/inbox", currentWorkspace?.id],
    queryFn: () => api.get("/signoffs/inbox"),
    enabled: !!user && !!currentWorkspace?.id && signoffsEnabled,
  });
  const signoffInboxCount = Array.isArray(signoffInbox) ? signoffInbox.length : 0;

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [dropdownOpen]);

  const isRTL = i18n.language === "ar";

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [i18n.language, isRTL]);
  
  const [pwaInstallOpen, setPwaInstallOpen] = useState(false);
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceLogo, setWorkspaceLogo] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [navSearch, setNavSearch] = useState("");
  const [staffPhoneLayout,setStaffPhoneLayout]=useState(()=>window.matchMedia("(max-width:767px)").matches);
  useEffect(()=>{
    const media=window.matchMedia("(max-width:767px)");
    const update=()=>setStaffPhoneLayout(media.matches);
    media.addEventListener("change",update);
    return()=>media.removeEventListener("change",update);
  },[]);

  useEffect(() => {
    if (!user) return;
    const pathToSection: Record<string, string> = {
      "/orders": "orders", "/transfers": "transfers", "/logistics": "orders",
        "/inventory": "inventory", "/inventory/recipes": "inventory", "/inventory/ingredients-review": "inventory", "/settings/item-groups": "inventory", "/admin/categories": "inventory", "/admin/finished-product-categories": "inventory",
      "/admin/brands": "inventory", "/admin/units": "inventory", "/admin/price-reasons": "inventory",
      "/branch-inventory": "inventory", "/inventory/safety-stocks": "inventory", "/stocktakes": "inventory", "/settings/count-schedules": "inventory",
      "/inventory/returns": "returns",
       "/suppliers": "procurement", "/delivery-companies": "procurement", "/purchase-orders": "procurement", "/supplier-receipts/changes": "procurement", "/supplier-receipts": "procurement", "/procurement/pending-pricing": "procurement", "/procurement/supplier-payments": "procurement",
       "/supplier-cycles": "procurement", "/settings/procurement": "procurement", "/purchase-requests": "procurement", "/procurement/requests-to-order": "procurement",
      "/inventory/waste": "waste",
      "/financials/branch-cash": "financials", "/financials": "financials", "/branch-comparison": "financials", "/branch-item-matrix": "financials",
      "/activity-log": "financials",
      "/reports/transactions": "reports",
      "/reports/combined-order-items": "reports",
      "/reports/procurement": "reports", "/reports/consumption": "reports", "/reports/sales-usage": "reports", "/reports/brand-mix": "reports",
      "/sales-upload": "inventory",
      "/admin/users": "admin", "/admin/locations": "admin", "/settings/roles": "admin",
      "/admin/vehicles": "admin",
      "/system/audit-log": "admin", "/settings/returns": "admin", "/admin/approval-bottleneck": "admin",
      "/admin/merge-duplicates": "admin",
       "/uniforms": "uniforms",
        "/staff": "staff",
        "/maintenance": "maintenance",
    };
    const match = Object.keys(pathToSection).find(p => location.startsWith(p));
    if (match) {
      const sectionId = pathToSection[match];
      setExpandedSections(prev => {
        const next = new Set(prev);
        next.add(sectionId);
        return next;
      });
    }
  }, [location, user]);

  const handleCreateWorkspace = async () => {
    if (!workspaceName.trim()) {
      toast({ title: t("common.error"), description: t("workspace.nameRequired"), variant: "destructive" });
      return;
    }
    
    setIsCreating(true);
    try {
      const newWorkspace = await api.workspaces.create({
        name: workspaceName.trim(),
        logo: workspaceLogo.trim() || undefined
      });
      
      toast({ title: t("common.success"), description: `${newWorkspace.name} - ${t("workspace.createdSuccess")}` });
      setCreateWorkspaceOpen(false);
      setWorkspaceName("");
      setWorkspaceLogo("");
      
      await refreshSession();
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message || t("workspace.failedToCreate"), variant: "destructive" });
    } finally {
      setIsCreating(false);
    }
  };

  if (!user) return <>{children}</>;

  // Helper to check permissions within the layout
  const hasPerm = (perm: any) => {
    return hasPermission(user, perm, roles);
  };
  
  // Get the user's role name for display
  const userRoleName = roles.find(r => r.id === user.roleId)?.name || user.roleId || 'User';
  const isAdminRole = user.roleId === "admin" || user.roleId === "system_admin" ||
    userRoleName === "Admin" || userRoleName === "Administrator" || userRoleName === "System Admin";
  const cashRoles = roles.map(role => ({ ...role, isSystem: false }));
  const cashView = hasPermission(user, "branch_cash.view", cashRoles);
  const cashBookView = hasPermission(user, "branch_cash.view_book", cashRoles);
  const cashBoxesView = hasPermission(user, "branch_cash.view_boxes", cashRoles);
  const cashHeldView = hasPermission(user, "branch_cash.view_held", cashRoles);
  const cashFinance = cashView && (["admin", "system_admin", "finance_manager"].includes(user.roleId || "") ||
    (roles.find(role => role.id === user.roleId)?.workspaceId === currentWorkspace?.id &&
      ["admin", "administrator", "system admin", "owner", "finance manager"].includes(userRoleName.trim().toLowerCase())));
  const cashLabel = (label: string) => cashText(label, isRTL);
  const explicitBrandRoles = roles.map(role => ({ ...role, isSystem: false }));
  const canManageItemBrands = isAdminRole ||
    hasPermission(user, "procurement.approve_po", explicitBrandRoles) ||
    hasPermission(user, "suppliers.manage", explicitBrandRoles);

  type NavItem = {
    href: string;
    label: string;
    permission: string | null;
    badge?: number;
    isNewOrder?: boolean;
    forceDocumentNavigation?: boolean;
  };
  type NavSection = { id: string; label: string; items: NavItem[] };
  type TopLevelItem = NavItem & { type: "top" };

  const isKoning = (currentWorkspace?.name || "").trim().toLowerCase() === "koning food industries";

  const topItems: TopLevelItem[] = [
    ...(hasPerm("dashboard.operations") || hasPerm("dashboard.analytics") ? [{ type: "top" as const, href: "/dashboard", label: t("nav.dashboard"), permission: null }] : []),
    { type: "top" as const, href: "/notifications", label: t("navSidebar.notifications"), permission: null, badge: unreadCount > 0 ? unreadCount : undefined },
    ...(signoffsEnabled ? [{ type: "top" as const, href: "/signoffs", label: t("navSidebar.signoffs"), permission: null, badge: signoffInboxCount > 0 ? signoffInboxCount : undefined }] : []),
    ...(hasPerm("analytics.lane_fulfillment") ? [{ type: "top" as const, href: "/analytics/lane-fulfillment", label: t("navSidebar.laneFulfillment"), permission: null }] : []),
  ];

  const sections: NavSection[] = [
    {
      id: "orders",
      label: t("navSidebar.sectionOrders"),
      items: [
        { href: "/orders", label: t("nav.orders"), permission: "view_orders", isNewOrder: hasPerm("create_orders") },
        { href: "/orders/templates", label: t("orders.templates"), permission: "create_orders" },
        ...((user.managedLocationIds?.length > 0 || isAdminRole) ? [{ href: "/orders/branch-templates", label: t("navSidebar.branchTemplates"), permission: "create_orders" }] : []),
        { href: "/transfers", label: t("navSidebar.transfers"), permission: "transfers.view" },
        ...((hasPerm("transfers.create") || (user as any).canInitiateTransfers) ? [{ href: "/internal-stock-requests", label: t("navSidebar.internalStockRequests"), permission: null }] : []),
        { href: "/logistics", label: t("navSidebar.logisticsCalendar"), permission: "logistics.view_calendar" },
      ],
    },
    ...(isKoning && hasPerm("customer_orders.manage") ? [{
      id: "customer-orders",
      label: t("navSidebar.sectionCustomerOrders"),
      items: [
        { href: "/customers", label: t("navSidebar.customers"), permission: "customer_orders.manage" },
        { href: "/customer-orders", label: t("nav.orders"), permission: "customer_orders.manage" },
        { href: "/customer-orders/new", label: t("navSidebar.newCustomerOrder"), permission: "customer_orders.manage" },
      ],
    }] : []),
    {
      id: "inventory",
      label: t("nav.inventory"),
      items: [
        { href: "/inventory", label: t("inventory.items"), permission: "inventory.items_page.view" },
        { href: "/inventory/recipes", label: t("recipes.nav", "Product Recipes"), permission: "inventory.recipes.manage" },
        { href: "/inventory/ingredients-review", label: t("navSidebar.ingredientsReview"), permission: "inventory.recipes.manage" },
        { href: "/sales-upload", label: t("salesConsumption.uploadTitle"), permission: "sales.upload" },
        { href: "/settings/item-groups", label: t("navSidebar.itemGroups"), permission: "view_brands" },
        { href: "/admin/categories", label: t("nav.categories"), permission: "manage_inventory" },
        { href: "/admin/finished-product-categories", label: t("nav.finishedProductCategories", "Finished product categories"), permission: "manage_categories" },
        { href: "/admin/brands", label: t("inventory.approvedBrands"), permission: "view_brands" },
        { href: "/admin/units", label: t("nav.units") || "Units", permission: "manage_inventory" },
        { href: "/admin/price-reasons", label: t("nav.priceReasons"), permission: "manage_prices" },
        { href: "/branch-inventory", label: t("navSidebar.branchInventory"), permission: "inventory.view" },
        { href: "/inventory/safety-stocks", label: t("navSidebar.safetyStocks"), permission: "inventory.manage_safety_stock" },
        { href: "/stocktakes", label: t("navSidebar.stocktakes"), permission: "stocktake.view" },
        { href: "/settings/count-schedules", label: t("navSidebar.countSchedules"), permission: "stocktake.manage_schedule" },
      ],
    },
    {
      id: "returns",
      label: t("navSidebar.returns"),
      items: [
        ...(hasPerm("returns.view") || hasPerm("returns.view_all") ? [{ href: "/inventory/returns", label: t("navSidebar.returns"), permission: null }] : []),
        ...(hasPerm("returns.receive") ? [{ href: "/inventory/returns/quarantine", label: t("navSidebar.quarantineItems"), permission: null }] : []),
        ...(hasPerm("returns.view_stats") ? [{ href: "/inventory/returns/stats", label: t("navSidebar.returnStats"), permission: null }] : []),
        ...(hasPerm("analytics.returns_waste.view") ? [{ href: "/inventory/returns-waste/analytics", label: t("returnsWaste.nav"), permission: null }] : []),
      ],
    },
    {
      id: "procurement",
      label: t("navSidebar.sectionProcurement"),
      items: [
        { href: "/suppliers", label: t("navSidebar.suppliers"), permission: "suppliers.view" },
        { href: "/delivery-companies", label: i18n.language.startsWith("ar") ? "شركات الديليفري" : "Delivery companies", permission: null },
        ...(canViewPurchaseRequests ? [{ href: "/purchase-requests", label: isRTL ? "طلبات الشراء" : "Purchase Requests", permission: null }] : []),
        ...(canProcessPurchaseRequests ? [{ href: "/procurement/requests-to-order", label: isRTL ? "طلبات بانتظار الشراء" : "Requests to order", permission: null, badge: processingCount?.count || undefined }] : []),
        { href: "/purchase-orders", label: t("navSidebar.purchaseOrders"), permission: "purchase_orders.view" },
        ...(canManageItemBrands ? [{ href: "/procurement/item-brands", label: "Item brands · الماركات", permission: null }] : []),
        { href: "/supplier-receipts", label: t("navSidebar.supplierReceipts"), permission: "supplier_receipts.view" },
        { href: "/procurement/receipt-quality", label: t("navSidebar.receiptQualityReview"), permission: "supplier_receipts.view" },
        { href: "/procurement/pending-pricing", label: "Pending pricing", permission: "supplier_receipts.edit_confirmed" },
        { href: "/procurement/supplier-payments", label: "Supplier payments", permission: "suppliers.payments.view" },
        { href: "/supplier-receipts/changes", label: "Receipt Changes", permission: "supplier_receipts.view" },
        { href: "/supplier-cycles", label: t("navSidebar.orderCycles"), permission: "supplier_cycles.view" },
        ...(user.managedLocationIds?.length > 0 ? [{ href: "/supplier-cycles/my-cycles", label: t("navSidebar.myCycles"), permission: "supplier_cycles.view" }] : []),
        { href: "/settings/procurement", label: t("navSidebar.procurementSettings"), permission: "procurement_settings.manage" },
      ],
    },
    {
      id: "waste",
      label: t("navSidebar.sectionWaste"),
      items: [
        ...(hasPerm("waste.log") || hasPerm("waste.view_all") || hasPerm("waste.approve") ? [{ href: "/inventory/waste", label: t("navSidebar.wasteLogs"), permission: null }] : []),
        ...(hasPerm("waste.manage_reasons") ? [{ href: "/inventory/waste/reasons", label: t("navSidebar.wasteReasons"), permission: null }] : []),
        ...(hasPerm("waste.manage_schedules") ? [{ href: "/inventory/waste/schedules", label: t("navSidebar.wasteSchedules"), permission: null }] : []),
        ...(hasPerm("waste.view_analytics") ? [{ href: "/inventory/waste/analytics", label: t("navSidebar.wasteAnalytics"), permission: null }] : []),
      ],
    },
    {
      id: "financials",
      label: t("nav.financials"),
      items: [
        { href: "/financials", label: t("financials.overview"), permission: "view_financials" },
        { href: "/branch-comparison", label: t("navSidebar.branchComparison"), permission: "analytics.branch_comparison" },
        { href: "/branch-item-matrix", label: t("navSidebar.branchItemOrders"), permission: "view_financials" },
        { href: "/financials/orders", label: t("orders.orderHistory"), permission: "view_financials" },
        { href: "/financials/audit", label: t("financials.audit"), permission: "view_financials" },
        { href: "/activity-log", label: t("navSidebar.activityLog"), permission: "analytics.activity_log" },
        ...(cashView ? [
          { href: "/financials/branch-cash", label: cashLabel("Overview"), permission: null },
          { href: "/financials/branch-cash/where-it-went", label: cashLabel("Where it went"), permission: null },
          { href: "/financials/branch-cash/daily-counts", label: cashLabel("Daily counts"), permission: null },
          { href: "/financials/branch-cash/things-to-fix", label: cashLabel("Things to fix"), permission: null },
        ] : []),
        ...(cashBookView ? [{ href: "/financials/branch-cash/cash-book", label: cashLabel("Cash book"), permission: null }] : []),
        ...(cashBoxesView ? [{ href: "/financials/branch-cash/cash-boxes", label: cashLabel("Cash boxes"), permission: null }] : []),
        ...(cashHeldView ? [
          { href: "/financials/branch-cash/personal-floats", label: cashLabel("Personal floats"), permission: null },
          { href: "/financials/branch-cash/who-holds-cash", label: cashLabel("Who holds cash"), permission: null },
        ] : []),
        ...(cashFinance ? [
          { href: "/financials/branch-cash/collections", label: cashLabel("Collections"), permission: null },
          { href: "/financials/branch-cash/insights", label: cashLabel("Insights"), permission: null },
          { href: "/financials/branch-cash/settings", label: cashLabel("Settings"), permission: null },
        ] : []),
        ...((cashView || hasPermission(user, "expense_setup.manage", cashRoles)) ? [
          { href: "/financials/branch-cash/expense-setup", label: cashLabel("Categories & line items"), permission: null },
        ] : []),
      ],
    },
    {
      id: "maintenance",
      label: maintenanceText("MAINTENANCE", isRTL),
      items: MAINTENANCE_PAGES.map((page,index)=>({
        href:page.path,label:maintenanceText(page.label,isRTL),permission:"maintenance.view",
        maintenanceGroup:page.group&&MAINTENANCE_PAGES[index-1]?.group!==page.group?maintenanceText(page.group,isRTL):undefined,
        maintenanceSub:!!page.group,
      })),
    },
    {
      id: "staff",
      label: staffText("STAFF", isRTL),
      items: [
        { href: "/staff", label: staffText("Overview", isRTL), permission: "employees.view" },
        { href: "/staff/attendance", label: isRTL?"الحضور":"Attendance", permission: "attendance.view" },
        { href: "/staff/employees", label: staffText("Employees", isRTL), permission: "employees.view" },
        ...(canTurnover?[{href:"/staff/turnover",label:isRTL?"دوران الموظفين":"Turnover",permission:"employees.view"}]:[]),
        { href: "/staff/positions", label: isRTL?"المسميات":"Positions", permission: "employees.view" },
        { href: "/staff/alerts", label: isRTL?"التنبيهات":"Alerts", permission: "employees.view" },
        ...(canActivity?[{href:"/staff/activity",label:isRTL?"النشاط":"Activity",permission:"employees.view"}]:[]),
        { href: "/staff/pay", label: staffText("Pay people", isRTL), permission: "staff.pay.view" },
        { href: "/staff/pay-records", label: staffText("Pay records", isRTL), permission: "staff.pay.view" },
        { href: "/staff/bonuses", label: staffText("Bonuses", isRTL), permission: "employees.view" },
        { href: "/staff/certificates-overview", label: staffText("Certificates overview", isRTL), permission: "employees.view" },
        { href: "/staff/certificates", label: staffText("Certificates", isRTL), permission: "employees.view" },
        { href: "/staff/advances", label: staffText("Advances", isRTL), permission: "employees.view" },
        { href: "/staff/deductions", label: staffText("Deductions", isRTL), permission: "employees.view" },
      ],
    },
    {
      id: "reports",
      label: t("navSidebar.sectionReports"),
      items: [
        { href: "/reports/transactions", label: t("navSidebar.transactionReport"), permission: "reports.transactions.view" },
        { href: "/reports/sales-usage", label: t("salesUsage.navLabel"), permission: "reports.consumption.view" },
        { href: "/reports/brand-mix", label: t("brandMix.title"), permission: "reports.consumption.view" },
        ...(hasPerm("reports.cost_view") || hasPerm("reports.cost_view_all") ? [
          { href: "/reports/combined-order-items", label: t("navSidebar.combinedOrderItemsReport"), permission: null as null },
          { href: "/reports/procurement",   label: "Procurement",   permission: null as null },
          { href: "/reports/replenishment", label: "Replenishment", permission: null as null },
        ] : []),
      ],
    },
    {
      id: "internal-market",
      label: t("navSidebar.sectionInternalMarket"),
      items: [
        { href: "/internal-market/buyer-groups", label: t("navSidebar.buyerGroups"), permission: "manage_price_lists" },
        { href: "/internal-market/price-lists", label: t("navSidebar.priceLists"), permission: "manage_price_lists" },
      ],
    },
    {
      id: "operations-catalog",
      label: t("navSidebar.catalog"),
      items: [
        { href: "/catalog", label: t("navSidebar.catalog"), permission: "view_internal_catalog" },
      ],
    },
    {
      id: "uniforms",
      label: t("navSidebar.uniforms"),
      items: [
        { href: "/uniforms", label: t("navSidebar.uniformOverview", "Uniforms overview"), permission: "uniforms.view" },
        { href: "/uniforms/issue", label: t("navSidebar.uniformIssue", "Issue uniforms"), permission: "uniforms.view" },
        { href: "/uniforms/handovers", label: t("navSidebar.uniformHandovers", "Handovers"), permission: "uniforms.view" },
        { href: "/uniforms/replacements", label: t("navSidebar.uniformReplacements", "Replacements"), permission: "uniforms.view" },
        { href: "/uniforms/clearance", label: t("navSidebar.uniformClearance", "Clearance"), permission: "uniforms.view" },
        { href: "/uniforms/issue-requests", label: t("navSidebar.uniformIssueRequests", "Over-cap requests"), permission: "uniforms.approve_over_cap" },
        { href: "/uniforms/garments", label: t("navSidebar.uniformGarments", "Garments"), permission: "uniforms.view" },
        { href: "/uniforms/policy", label: t("navSidebar.uniformPolicy", "Policy"), permission: "uniforms.view" },
        { href: "/uniforms/policy/settings", label: t("navSidebar.uniformSettings", "Settings"), permission: "uniforms.view" },
      ],
    },
    {
      id: "admin",
      label: t("admin.title"),
      items: [
        { href: "/admin/locations", label: t("nav.locations"), permission: "manage_locations" },
        { href: "/admin/vehicles", label: t("vehicles.title"), permission: null },
        { href: "/admin/feature-flags", label: t("rules.settings.navLabel", "Feature Flags"), permission: "manage_users" },
        { href: "/admin/rules", label: t("rules.list.navLabel", "Order Rules"), permission: "rules.manage" },
        { href: "/admin/rules/audit", label: t("rules.audit.navLabel", "Rules Audit Log"), permission: "rules.view_audit" },
        { href: "/orders/bulk-upload", label: t("bulk_upload.navLabel", "Bulk Order Upload"), permission: "orders.bulk_upload" },
        { href: "/system/audit-log", label: t("navSidebar.auditLog"), permission: "audit_trail.view" },
        { href: "/settings/returns", label: t("navSidebar.returnSettings"), permission: "returns.manage_settings" },
        ...((isAdminRole || hasPerm("orders.view_all") || hasPerm("view_all_orders")) ? [{ href: "/admin/approval-bottleneck", label: t("navSidebar.approvalBottleneck"), permission: null }] : []),
        {
          href: "/admin/merge-duplicates",
          label: t("navSidebar.mergeDuplicates"),
          permission: "manage_users",
          forceDocumentNavigation: true,
        },
      ],
    },
    ...(canOpenPeopleAccess(user, roles) || canEnterPeopleAccess ? [{
      id: "people-access",
      label: "People & access",
      items: [
        { href: "/people-access/people", label: "People", permission: null },
        { href: "/people-access/roles", label: "Roles", permission: null },
        { href: "/people-access/locations", label: "Locations", permission: null },
        { href: "/people-access/sensitive-numbers", label: "Sensitive numbers", permission: null },
        { href: "/people-access/compare", label: "Compare access", permission: null },
      ],
    }] : []),
  ];

  const standAloneItems: NavItem[] = [
    { href: "/branch-profile", label: t("navSidebar.branchProfile"), permission: "view_orders" },
    ...(user.roleId === "system_admin" ? [{ href: "/super-admin", label: t("navSidebar.superAdmin"), permission: null }] : []),
  ];

  const filterItems = (items: NavItem[]) => items.filter(i => i.permission === null || hasPerm(i.permission));

  const navSearchQ = navSearch.trim().toLowerCase();
  const matchesSearch = (label: string) => !navSearchQ || (label || "").toLowerCase().includes(navSearchQ);

  const filteredTopItems = topItems.filter(i => matchesSearch(i.label));

  const filteredSections = sections
    .map(s => ({ ...s, items: filterItems(s.items).filter(i => matchesSearch(i.label)) }))
    .filter(s => s.items.length > 0);

  const filteredStandAlone = filterItems(standAloneItems).filter(i => matchesSearch(i.label));

  // When searching, auto-expand all sections so matches are visible
  const isSearching = navSearchQ.length > 0;

  const findSectionForPath = (path: string): string | null => {
    for (const s of filteredSections) {
      if (s.items.some(i => path.startsWith(i.href))) return s.id;
    }
    return null;
  };

  const toggleSection = (id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const SidebarContent = () => (
    <div className={cn("flex flex-col h-full bg-sidebar text-sidebar-foreground border-sidebar-border", isRTL ? "border-s" : "border-e")}>
      {/* Workspace Switcher */}
      <div className="p-4 border-b border-sidebar-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start px-2 py-2 hover:bg-sidebar-accent hover:text-white h-auto">
              <div className="flex items-center gap-3 w-full">
                <div className="h-8 w-8 rounded bg-white p-0.5 flex-shrink-0 overflow-hidden">
                  <img 
                    src={currentWorkspace?.logo || logo} 
                    alt="Logo" 
                    className="h-full w-full object-contain" 
                  />
                </div>
                <div className="flex flex-col items-start flex-1 overflow-hidden">
                  <span className="text-sm font-semibold truncate w-full text-start text-white leading-tight">
                    {currentWorkspace?.name || "Infrastock"}
                  </span>
                  <span className="text-xs text-slate-500 truncate w-full text-start capitalize">
                    {isRTL && userRoleName === "Administrator" ? "مدير النظام" : userRoleName}
                  </span>
                </div>
                <ChevronsUpDown className="h-4 w-4 text-slate-500" />
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56 bg-sidebar border-sidebar-border text-sidebar-foreground" align="start" forceMount>
            <DropdownMenuLabel className="text-sidebar-foreground/70">{t("workspace.switchWorkspace")}</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-sidebar-border" />
            {availableWorkspaces.map(ws => (
              <DropdownMenuItem 
                key={ws.id} 
                onClick={() => switchWorkspace(ws.id)}
                className="focus:bg-sidebar-accent focus:text-sidebar-accent-foreground cursor-pointer gap-2"
              >
                <div className="h-6 w-6 rounded-sm bg-white border overflow-hidden flex-shrink-0">
                  <img src={ws.logo} alt={ws.name} className="h-full w-full object-cover" />
                </div>
                <span className={ws.id === currentWorkspace?.id ? "font-medium" : ""}>{ws.name}</span>
                {ws.id === currentWorkspace?.id && <Check className="ms-auto h-3 w-3" />}
              </DropdownMenuItem>
            ))}
            <CreateWorkspaceMenuItem user={user} roles={roles} onCreate={() => setCreateWorkspaceOpen(true)} />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Sidebar Search */}
      {hasPerm("employees.view") && <div className="staff-ui staff-search-only" dir={isRTL ? "rtl" : "ltr"} lang={isRTL ? "ar" : "en"}><Suspense fallback={null}><StaffStyles /></Suspense><StaffPersonSearch /></div>}
      <div className="px-3 pt-3 pb-1">
        <div className="relative">
          <input
            type="text"
            value={navSearch}
            onChange={(e) => setNavSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") setNavSearch(""); }}
            placeholder={t("navSidebar.searchPlaceholder")}
            className="w-full h-8 rounded-md bg-sidebar-accent/30 border border-sidebar-border px-3 text-xs text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-sidebar-primary"
            data-testid="input-sidebar-search"
          />
        </div>
      </div>

      <nav className="flex-1 py-3 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-sidebar [&::-webkit-scrollbar-thumb]:bg-slate-600 [&::-webkit-scrollbar-thumb]:rounded-full">
        {filteredTopItems.length === 0 && filteredSections.length === 0 && filteredStandAlone.length === 0 && (
          <p className="px-4 py-3 text-xs text-slate-400" data-testid="text-sidebar-no-results">{t("navSidebar.noMatches")}</p>
        )}
        {filteredTopItems.map((item) => {
          const isActive = location.startsWith(item.href);
          return (
            <Link key={item.href} href={item.href}>
              <div className={cn(
                "flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors duration-200 cursor-pointer",
                isActive ? "bg-sidebar-accent text-white" : "text-slate-300 hover:bg-sidebar-accent hover:text-white"
              )}>
                {item.href === "/dashboard" ? <LayoutDashboard className={cn("h-5 w-5", !isActive && "opacity-70")} /> : item.href === "/signoffs" ? <Shield className={cn("h-5 w-5", !isActive && "opacity-70")} /> : <Bell className={cn("h-5 w-5", !isActive && "opacity-70")} />}
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span className="h-5 min-w-[20px] px-1 rounded-full bg-sidebar-primary text-white text-[10px] font-bold flex items-center justify-center leading-none">
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                )}
              </div>
            </Link>
          );
        })}

        <div className="my-2 mx-4 border-t border-sidebar-border" />

        {filteredSections.map((section) => {
          const isExpanded = isSearching || expandedSections.has(section.id);

          return (
            <div key={section.id}>
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center gap-3 px-4 py-2 text-start cursor-pointer transition-colors duration-200 text-white hover:text-white"
              >
                <span className="text-[10px] font-bold uppercase tracking-widest flex-1">{section.label}</span>
                <ChevronRight className={cn("h-3 w-3 transition-transform duration-200", isExpanded && "rotate-90")} />
              </button>

              <div className={cn(
                "overflow-hidden transition-all duration-200",
                isExpanded ? section.id==="maintenance"?"max-h-[1200px] opacity-100":"max-h-[600px] opacity-100" : "max-h-0 opacity-0"
              )}>
                {section.items.map((item) => {
                  const isActive = item.href === "/financials" || item.href === "/financials/branch-cash"
                    ? location === item.href
                    : item.href === "/staff" || section.id==="maintenance" ? location === item.href : location === item.href || location.startsWith(`${item.href}/`);
                  const itemContent = (
                    <div className={cn(
                      "w-full flex items-center gap-3 ps-8 pe-4 py-2 text-start transition-colors duration-200 cursor-pointer",
                      section.id==="maintenance" && (item as any).maintenanceSub && "ps-11",
                      isActive ? "bg-sidebar-accent text-white" : "text-white/60 hover:text-white hover:bg-white/5"
                    )}>
                      <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", isActive ? "bg-white" : "bg-white/20")} />
                      <span className="text-sm flex-1">{item.label}{item.href==="/maintenance/library"&&<LibraryPendingBadge rtl={isRTL}/>} {item.href==="/staff/alerts"&&<StaffAlertsUnread/>}{item.href==="/staff/attendance"&&<StaffAttendancePendingBadge/>}{item.href==="/staff/certificates"&&<StaffCertificateBadge/>}</span>
                      {item.href==="/purchase-requests"&&!!purchaseRequestCount?.count&&<Badge data-testid="pr-nav-count" className="ms-auto h-5 min-w-5 px-1.5 text-[10px]">{String(purchaseRequestCount.count)}</Badge>}
                      {item.href==="/procurement/requests-to-order"&&!!item.badge&&<Badge data-testid="processing-nav-count" className="ms-auto h-5 min-w-5 px-1.5 text-[10px]">{String(item.badge)}</Badge>}
                    </div>
                  );
                  return (
                    <div key={item.href}>
                      {section.id==="maintenance" && (item as any).maintenanceGroup && <div className="ps-8 pe-4 pt-3 pb-1 text-xs font-semibold text-white/80">{(item as any).maintenanceGroup}</div>}
                       {section.id === "financials" && item.href === "/financials/branch-cash" && <div className="ps-8 pe-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{cashLabel("Branch cash")}</div>}
                      <div className="flex items-center">
                      <div className="flex-1 min-w-0">
                      {item.forceDocumentNavigation ? (
                        <a href={item.href}>{itemContent}</a>
                      ) : (
                        <Link href={item.href}>{itemContent}</Link>
                      )}
                      </div>
                      {item.href === "/purchase-orders" && <div className="pe-4"><ReceivedBeforeApprovalCount /></div>}
                      </div>
                      {item.isNewOrder && (
                        <div className="flex rounded-md overflow-hidden mx-4 ms-8 me-4 mb-1">
                          <Button
                            size="sm"
                            variant="secondary"
                            className="gap-1.5 rounded-e-none flex-1 text-xs h-7 border-e border-border/50"
                            data-testid="button-sidebar-new-order"
                            onClick={() => navigate("/orders/new")}
                          >
                            <Plus className="h-3 w-3" /> {t("navSidebar.newOrder")}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="secondary"
                                className="rounded-s-none px-2 h-7"
                                data-testid="button-sidebar-new-order-chevron"
                                aria-label={t("navSidebar.moreOrderOptions")}
                              >
                                <ChevronDown className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="right" align="start" className="w-48">
                              <DropdownMenuItem onClick={() => navigate("/orders/new")} className="cursor-pointer" data-testid="menu-sidebar-single-order">
                                <ListOrdered className="h-4 w-4 me-2" /> {t("navSidebar.singleOrder")}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate("/orders/batch-create")} className="cursor-pointer" data-testid="menu-sidebar-batch-orders">
                                <CalendarDays className="h-4 w-4 me-2" /> {t("navSidebar.batchOrders")}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem disabled data-testid="menu-sidebar-weekly-planner" className="opacity-60">
                                <CalendarRange className="h-4 w-4 me-2" /> {t("navSidebar.weeklyPlanner")}
                                <Badge variant="outline" className="ms-auto text-xs py-0 h-4">{t("navSidebar.comingSoon")}</Badge>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {filteredStandAlone.length > 0 && (
          <>
            <div className="my-2 mx-4 border-t border-sidebar-border" />
            {filteredStandAlone.map((item) => {
              const isActive = location.startsWith(item.href);
              return (
                <Link key={item.href} href={item.href}>
                  <div className={cn(
                    "flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors duration-200 cursor-pointer",
                    isActive ? "bg-sidebar-accent text-white" : "text-slate-300 hover:bg-sidebar-accent hover:text-white"
                  )}>
                    <span className="flex-1">{item.label}{item.href==="/maintenance/library"&&<LibraryPendingBadge rtl={isRTL}/>} {item.href==="/staff/alerts"&&<StaffAlertsUnread/>}</span>
                  </div>
                </Link>
              );
            })}
          </>
        )}
      </nav>

      <div className="p-4 border-t border-sidebar-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start px-2 py-2 hover:bg-sidebar-accent hover:text-white h-auto">
              <div className="flex items-center gap-3 w-full">
                <div className="h-8 w-8 rounded-full bg-sidebar-primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {user.name?.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2) || "U"}
                </div>
                <div className="flex flex-col items-start flex-1 overflow-hidden">
                  <span className="text-sm font-semibold truncate w-full text-start text-white leading-tight">{user.name}</span>
                  <span className="text-xs text-slate-500 truncate w-full text-start capitalize">{isRTL && userRoleName === "Administrator" ? "مدير النظام" : userRoleName}</span>
                </div>
                <ChevronsUpDown className="h-4 w-4 text-slate-500" />
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56 bg-sidebar border-sidebar-border text-sidebar-foreground" align="end" forceMount>
            <Link href="/settings">
              <DropdownMenuItem className="focus:bg-sidebar-accent focus:text-sidebar-accent-foreground cursor-pointer">
                <Settings className="me-2 h-4 w-4" />
                {t("nav.settings")}
              </DropdownMenuItem>
            </Link>
            <Link href="/settings/notifications">
              <DropdownMenuItem
                className="focus:bg-sidebar-accent focus:text-sidebar-accent-foreground cursor-pointer"
                data-testid="link-notification-preferences"
              >
                <Bell className="me-2 h-4 w-4" />
                {t("navSidebar.notificationPreferences")}
              </DropdownMenuItem>
            </Link>
            <DropdownMenuItem
              onClick={() => setPwaInstallOpen(true)}
              className="focus:bg-sidebar-accent focus:text-sidebar-accent-foreground cursor-pointer"
              data-testid="menuitem-get-mobile-app"
            >
              <Smartphone className="me-2 h-4 w-4" />
              {t("navSidebar.getMobileApp")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={logout} className="text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer">
              <LogOut className="me-2 h-4 w-4" />
              {t("nav.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <StaffEmployeeOverlayProvider>
    <div className="min-h-screen flex bg-background">
      {/* Desktop Sidebar */}
      <aside className={cn("hidden md:block w-64 fixed inset-y-0 z-30", isRTL ? "end-0" : "start-0")}>
        {SidebarContent()}
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
        <SheetContent side={isRTL ? "right" : "left"} className={cn("p-0 w-64 bg-sidebar", isRTL ? "border-s-sidebar-border" : "border-e-sidebar-border")}>
          {SidebarContent()}
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <main className={cn("flex-1 min-w-0 flex flex-col min-h-screen transition-all duration-300 ease-in-out", isRTL ? "md:me-64" : "md:ms-64")}>
        {/* Header */}
        <header className={cn("border-b backdrop-blur-sm sticky top-0 z-20 px-3 md:px-6 flex items-center justify-between", isDeliveryCompanies ? "h-[60px] bg-white" : "h-14 md:h-16 bg-card/80")}>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setIsMobileOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <nav aria-label="Breadcrumb" className="hidden md:flex items-center text-xs uppercase tracking-wider font-medium text-slate-400">
              <span className="hover:text-slate-600 cursor-pointer transition-colors">{currentWorkspace?.name || t("navSidebar.workspace")}</span>
              <span className="mx-2 text-slate-300">/</span>
              {isDeliveryCompanies
                ? <><span className={deliveryCompany ? "" : "text-slate-900"}>{i18n.language.startsWith("ar") ? "المشتريات" : "PROCUREMENT"}</span>{deliveryCompany && <><span className="mx-2 text-slate-300">/</span><span className="text-slate-900">{i18n.language.startsWith("ar") ? deliveryCompany.ar : deliveryCompany.en}</span></>}</>
                : location.split('/')[1] === "maintenance"
                ? <Link href="/maintenance/jobs" className="text-slate-900">{maintenanceText("MAINTENANCE", isRTL)}</Link>
                : <span className="text-slate-900">{location.split('/')[1]?.replace(/-/g, ' ') || t("nav.dashboard")}</span>}
            </nav>
          </div>
          
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <div className="relative" ref={dropdownRef}>
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => setDropdownOpen((prev) => !prev)}
                data-testid="button-notifications"
              >
                <Bell className="h-5 w-5 text-muted-foreground" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -end-1 h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Button>

              {dropdownOpen && (
                <div className="absolute end-0 top-full mt-2 w-[400px] max-h-[520px] bg-card border border-border rounded-xl shadow-xl z-50 flex flex-col overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <h3 className="text-sm font-medium uppercase tracking-wide">{t("navSidebar.notifications")}</h3>
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs h-auto py-1"
                        onClick={() => markAllRead()}
                        data-testid="button-mark-all-read"
                      >
                        {t("navSidebar.markAllRead")}
                      </Button>
                    )}
                  </div>

                  {/* List */}
                  <div className="overflow-y-auto flex-1">
                    {notifications.length === 0 ? (
                      <div className="py-12 text-center">
                        <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">{t("navSidebar.noNotificationsYet")}</p>
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((n: any) => (
                        <NotificationRow
                          key={n.id}
                          notification={n}
                          compact
                          onRead={markRead}
                          onNavigate={(link) => { navigate(link); setDropdownOpen(false); }}
                        />
                      ))
                    )}
                  </div>

                  {/* Footer */}
                  {notifications.length > 0 && (
                    <div className="px-4 py-2.5 border-t border-border text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-primary"
                        onClick={() => { navigate("/notifications"); setDropdownOpen(false); }}
                        data-testid="button-view-all-notifications"
                      >
                        {t("navSidebar.viewAllNotifications")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        <PwaInstallBanner onOpenDialog={() => setPwaInstallOpen(true)} />

        {/* Page Content */}
        <div className="flex-1 overflow-auto">
          <div data-delivery-shell={isDeliverySurface ? "true" : undefined} className={isDeliverySurface || location === "/staff" || location.startsWith("/staff/") || location === "/maintenance" || location.startsWith("/maintenance/") ? "" : "p-4 sm:p-6 animate-in fade-in slide-in-from-bottom-4 duration-500"}>
            {location === "/staff" || location.startsWith("/staff/") ? <div className={`staff-ui staff-content${staffPhoneLayout?" phone":""}`} dir={isRTL ? "rtl" : "ltr"} lang={isRTL ? "ar" : "en"}><Suspense fallback={null}><StaffStyles /></Suspense>{children}</div> : children}
          </div>
        </div>
      </main>

      <PwaInstallDialog open={pwaInstallOpen} onOpenChange={setPwaInstallOpen} />

      {/* Create Workspace Dialog */}
      <Dialog open={createWorkspaceOpen} onOpenChange={setCreateWorkspaceOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("workspace.createNewWorkspace")}</DialogTitle>
            <DialogDescription>
              {t("workspace.createDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="workspace-name">{t("workspace.workspaceName")} *</Label>
              <Input
                id="workspace-name"
                placeholder={t("navSidebar.workspaceNamePlaceholder")}
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                data-testid="input-workspace-name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="workspace-logo">{t("workspace.logoUrl")}</Label>
              <Input
                id="workspace-logo"
                placeholder="https://example.com/logo.png"
                value={workspaceLogo}
                onChange={(e) => setWorkspaceLogo(e.target.value)}
                data-testid="input-workspace-logo"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateWorkspaceOpen(false)}
              disabled={isCreating}
            >
              {t("common.cancel")}
            </Button>
            <Button 
              onClick={handleCreateWorkspace} 
              disabled={isCreating}
              data-testid="button-submit-workspace"
            >
              {isCreating && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {t("workspace.createWorkspace")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </StaffEmployeeOverlayProvider>
  );
}
